import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, model, signal } from "@angular/core";
import { InstanceCount } from "../../util/s25.instance.count";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25qlSearchSimpleInputComponent } from "./s25ql.search.simple.input.component";
import type { KeywordCriteria } from "./search";
import type { SimpleSearch } from "./simple.search.criteria";

@Component({
    selector: "s25ql-search-simple-input-dropdown",
    host: {
        class: "search-filters-container",
    },
    template: `
        <button
            class="search-filters-toggle c-input"
            [attr.aria-expanded]="open()"
            [attr.aria-controls]="panelId"
            (click)="open.set(!open())"
        >
            Search Filters
            <s25-ng-icon
                class="dropdown-icon"
                [class.dropdown-icon--open]="open()"
                [type]="'caretDown'"
                [ariaLabel]="false"
            />
        </button>
        <div
            class="search-filters-panel"
            [class.search-filters-panel--fixed]="fixedOnBody()"
            [id]="panelId"
            [hidden]="!open()"
        >
            @defer {
                <s25-ng-ql-search-simple-input
                    [type]="type()"
                    [criteria]="criteria()"
                    (criteriaChange)="criteria.set($event)"
                />
            }
        </div>
    `,
    styleUrls: ["s25ql.search.simple.input.dropdown.component.scss"],
    standalone: true,
    imports: [S25IconComponent, S25qlSearchSimpleInputComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25qlSearchSimpleInputDropdown extends InstanceCount {
    readonly type = input.required<SimpleSearch.TypeId>();
    readonly criteria = model<KeywordCriteria>();
    readonly fixedOnBody = input(false);

    private readonly hostElement = inject(ElementRef<HTMLElement>);

    readonly open = signal(false);

    protected readonly panelId = `s25-ql-search-simple-input-dropdown-${this.instanceId}`;

    constructor() {
        super();
        effect((onCleanup) => {
            if (!this.open()) return;
            const handler = (clickEvent: MouseEvent) => {
                const target = clickEvent.target as Node;
                // Ignore clicks on the toggle button itself (it has its own handler)
                // and on popovers that are rendered outside the dropdown.
                const isOnHost = this.hostElement.nativeElement.contains(target);
                const isInsidePopover = (target as Element).closest?.(".popover");
                if (!isOnHost && !isInsidePopover) this.open.set(false);
            };
            document.addEventListener("click", handler, true);
            onCleanup(() => document.removeEventListener("click", handler, true));
        });
    }
}
